import React, { useState, useMemo } from "react";
import { useTrail, animated } from "@react-spring/web";
import {
  Badge,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@ncoder/ui";
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  FileCode,
  Loader2,
  Sparkles,
} from "lucide-react";
import { ComponentRow, type ComponentImproveArg } from "@/components/atoms/salesforce-release/ComponentRow";
import type {
  FindingOut,
  ImpactBucket,
  ReleaseDocument,
} from "@/services/salesforce-release";

const BUCKETS: ImpactBucket[] = ["Breaking", "Deprecated", "Review", "Improvement", "OK"];
const PAGE_SIZES = [10, 20, 50, 100];

interface Props {
  findings: FindingOut[];
  isLoading?: boolean;
  componentTypes: string[];
  searchTerm: string;
  selectedBucket: string;
  selectedType: string;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalComponents: number;
  totalFindings: number;
  onSearchChange: (v: string) => void;
  onBucketChange: (v: string) => void;
  onTypeChange: (v: string) => void;
  onPageChange: (n: number) => void;
  onPageSizeChange: (n: number) => void;
  // Optional secondary chrome — kept in the header so the page above stays clean.
  docs?: ReleaseDocument[];
  activeReleaseId?: string | null;
  onPickDoc?: (id: string) => void;
  liveStatusLabel?: string | null;
  liveStatusPct?: number | null;
  liveStatusError?: string | null;
  // "Improve with AI" handoff to the multi-agent chat.
  onImproveWithAI?: () => void;
  canImprove?: boolean;
  // Per-component "Fix with AI" handoff (one button per finding row).
  onImproveComponent?: (group: ComponentImproveArg) => void;
}

interface ComponentGroup {
  componentRef: string;
  componentType: string;
  componentName: string;
  findings: FindingOut[];
}

function groupByComponent(findings: FindingOut[]): ComponentGroup[] {
  const map = new Map<string, ComponentGroup>();
  for (const f of findings) {
    const key = f.component_ref;
    if (!map.has(key)) {
      map.set(key, {
        componentRef: key,
        componentType: f.component_type,
        componentName: f.component_name,
        findings: [],
      });
    }
    map.get(key)!.findings.push(f);
  }
  return Array.from(map.values());
}

function SkeletonRows() {
  return (
    <div className="space-y-1 p-4">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="flex items-center gap-4 border-b border-border/50 p-4">
          <div className="loading-parallax h-5 w-5 rounded bg-muted-foreground/10" />
          <div className="loading-parallax h-5 w-5 rounded bg-muted-foreground/10" />
          <div className="flex-1 space-y-1.5">
            <div className="loading-parallax h-4 w-48 rounded-md bg-muted-foreground/10" />
            <div className="loading-parallax h-3 w-32 rounded-md bg-muted-foreground/[0.07]" />
          </div>
          <div className="loading-parallax h-6 w-20 rounded bg-muted-foreground/10" />
        </div>
      ))}
    </div>
  );
}

export const ImpactFindingsTable: React.FC<Props> = ({
  findings,
  isLoading,
  componentTypes,
  searchTerm,
  selectedBucket,
  selectedType,
  currentPage,
  pageSize,
  totalPages,
  totalComponents,
  totalFindings,
  onSearchChange,
  onBucketChange,
  onTypeChange,
  onPageChange,
  onPageSizeChange,
  docs,
  activeReleaseId,
  onPickDoc,
  liveStatusLabel,
  liveStatusPct,
  liveStatusError,
  onImproveWithAI,
  canImprove,
  onImproveComponent,
}) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const grouped = useMemo(() => {
    let g = groupByComponent(findings);
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      g = g.filter(
        (gg) =>
          gg.componentName.toLowerCase().includes(q) ||
          gg.componentRef.toLowerCase().includes(q) ||
          gg.findings.some(
            (f) =>
              (f.rationale ?? "").toLowerCase().includes(q) ||
              (f.fix_action ?? "").toLowerCase().includes(q) ||
              (f.deprecated_or_changed_symbol ?? "").toLowerCase().includes(q)
          )
      );
    }
    return g;
  }, [findings, searchTerm]);

  const totalRowsInView = grouped.length;

  // Row entrance stagger — re-triggers on filter/search/page change, same
  // recipe as the heatmap rows and the Git page's list rows.
  const rowTrail = useTrail(grouped.length, {
    from: { opacity: 0, transform: "translateY(4px)" },
    to: { opacity: 1, transform: "translateY(0px)" },
    config: { tension: 280, friction: 34, clamp: true },
  });

  const toggle = (ref: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(ref)) next.delete(ref);
      else next.add(ref);
      return next;
    });
  };
  const expandAll = () => setExpanded(new Set(grouped.map((g) => g.componentRef)));
  const collapseAll = () => setExpanded(new Set());

  const startItem = totalComponents === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalComponents);

  return (
    <div className="flex flex-col rounded-xl border border-border bg-card overflow-hidden">
      <div className="flex-shrink-0 space-y-3 border-b border-border px-5 py-4">
        {/* Top row: title · doc picker · live status */}
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="flex items-center gap-2 flex-shrink-0 text-sm font-semibold">
            <AlertTriangle className="h-4 w-4 text-yellow-500" />
            Impact Findings
          </h3>

          {docs && docs.length > 0 && onPickDoc ? (() => {
            const selectedDoc = docs.find((d) => d.release_id === activeReleaseId);
            // The @ncoder/ui SelectTrigger has no overflow clipping and Radix renders
            // the value as a bare text node, so `truncate` never applied — a long PDF
            // filename spilled out. Render the value as an explicit truncating span
            // and clip the trigger so it can't overflow; items + hover title too.
            return (
              <Select value={activeReleaseId ?? undefined} onValueChange={onPickDoc}>
                <SelectTrigger className="w-full max-w-72 sm:w-72 h-8 overflow-hidden">
                  <SelectValue placeholder="Pick a release doc">
                    <span
                      className={`block min-w-0 truncate text-left ${selectedDoc ? "" : "text-muted-foreground"}`}
                      title={selectedDoc ? `${selectedDoc.filename} · ${selectedDoc.status}` : undefined}
                    >
                      {selectedDoc ? `${selectedDoc.filename} · ${selectedDoc.status}` : "Pick a release doc"}
                    </span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent position="popper" className="max-w-[24rem]">
                  {docs.map((d) => (
                    <SelectItem key={d.release_id} value={d.release_id}>
                      <span className="block max-w-[22rem] truncate" title={`${d.filename} · ${d.status}`}>
                        {d.filename} · {d.status}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            );
          })() : null}

          {liveStatusError ? (
            <Badge variant="outline" className="text-destructive border-destructive">
              {liveStatusError}
            </Badge>
          ) : liveStatusLabel ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              <span>{liveStatusLabel}</span>
              {liveStatusPct != null ? (
                <span className="font-mono">· {liveStatusPct}%</span>
              ) : null}
              {liveStatusPct != null ? (
                <div className="ml-1 h-1 w-32 overflow-hidden rounded bg-muted">
                  <div className="h-full bg-primary transition-all" style={{ width: `${liveStatusPct}%` }} />
                </div>
              ) : null}
            </div>
          ) : null}

        </div>

        {/* Filter row */}
        <div className="flex items-center gap-4 flex-wrap">

          <div className="flex items-center gap-2 flex-1 flex-wrap min-w-0">
            <Input
              placeholder="Search component / rule / fix…"
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="min-w-0 flex-1"
            />
            <Select value={selectedBucket} onValueChange={onBucketChange}>
              <SelectTrigger className="shrink-0 w-36">
                <SelectValue placeholder="Bucket" />
              </SelectTrigger>
              <SelectContent position="popper">
                <SelectItem value="all">All buckets</SelectItem>
                {BUCKETS.map((b) => (
                  <SelectItem key={b} value={b}>{b}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedType} onValueChange={onTypeChange}>
              <SelectTrigger className="shrink-0 w-44">
                <SelectValue placeholder="Component type" />
              </SelectTrigger>
              <SelectContent position="popper">
                <SelectItem value="all">All types</SelectItem>
                {componentTypes.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {grouped.length > 0 && (
            <div className="flex items-center gap-1 text-sm flex-shrink-0">
              <button onClick={expandAll} className="text-muted-foreground hover:text-foreground">
                Expand All
              </button>
              <span className="text-muted-foreground">|</span>
              <button onClick={collapseAll} className="text-muted-foreground hover:text-foreground">
                Collapse All
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex-1">
        {isLoading ? (
          <SkeletonRows />
        ) : grouped.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <FileCode className="h-12 w-12 mb-4 opacity-50" />
            <p className="text-lg font-medium">No findings match these filters</p>
            <p className="text-sm">Try clearing filters or running a fresh scan</p>
          </div>
        ) : (
          <div>
            {rowTrail.map((style, i) => {
              const g = grouped[i];
              return (
                <animated.div key={g.componentRef} style={style}>
                  <ComponentRow
                    componentRef={g.componentRef}
                    componentType={g.componentType}
                    componentName={g.componentName}
                    findings={g.findings}
                    isExpanded={expanded.has(g.componentRef)}
                    onToggle={toggle}
                    onImproveWithAI={onImproveComponent}
                  />
                </animated.div>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex-shrink-0 border-t border-border px-5 py-3">
        {totalFindings > 0 && (
          <div className="flex items-center justify-between w-full flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                Showing {startItem}-{endItem} of {totalComponents} components ({totalFindings} findings, {totalRowsInView} in view)
              </span>
              <Select value={pageSize.toString()} onValueChange={(v) => onPageSizeChange(Number(v))}>
                <SelectTrigger className="w-20 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZES.map((n) => (
                    <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">per page</span>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage <= 1 || isLoading}
              >
                <ChevronLeft className="h-4 w-4" /> Previous
              </Button>
              <span className="text-sm text-muted-foreground px-2">
                Page {currentPage} of {totalPages || 1}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage >= totalPages || isLoading}
              >
                Next <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImpactFindingsTable;
